import { Link } from "@tanstack/react-router";
import { Bug, CheckCircle2, Lightbulb, Send } from "lucide-react";
import { type FormEvent, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { authClient } from "@/lib/auth-client";
import { client } from "@/orpc/client";
import { m } from "@/paraglide/messages";

type ContactType = "proposal" | "bug";

const TITLE_MIN = 3;
const TITLE_MAX = 120;
const MESSAGE_MIN = 10;
const MESSAGE_MAX = 2000;

const TYPE_META: Record<
  ContactType,
  {
    label: () => string;
    icon: typeof Lightbulb;
    titleHint: () => string;
    bodyHint: () => string;
  }
> = {
  proposal: {
    label: m.contact_type_proposal,
    icon: Lightbulb,
    titleHint: m.contact_proposal_title_hint,
    bodyHint: m.contact_proposal_body_hint,
  },
  bug: {
    label: m.contact_type_bug,
    icon: Bug,
    titleHint: m.contact_bug_title_hint,
    bodyHint: m.contact_bug_body_hint,
  },
};

function TypeButton({
  type,
  active,
  onClick,
}: {
  type: ContactType;
  active: boolean;
  onClick: () => void;
}) {
  const meta = TYPE_META[type];
  const Icon = meta.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`flex flex-1 items-center justify-center gap-2 rounded-lg border px-4 py-3 text-sm font-medium transition-all ${
        active
          ? "border-primary bg-primary/10 text-primary"
          : "border-border bg-card text-muted-foreground hover:border-primary/40 hover:text-foreground"
      }`}
    >
      <Icon className="h-4 w-4" aria-hidden="true" />
      {meta.label()}
    </button>
  );
}

export function ContactForm() {
  const { data: session, isPending } = authClient.useSession();
  const [type, setType] = useState<ContactType>("proposal");
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (type === "proposal") {
        await client.contact.submitProposal({ title, message });
      } else {
        await client.contact.submitBug({ title, message });
      }
      setSubmitted(true);
      setTitle("");
      setMessage("");
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : m.contact_error_generic();
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  // While the session resolves, render nothing to avoid a flash of the wrong state.
  if (isPending) {
    return (
      <div className="h-48 animate-pulse rounded-xl border border-border bg-card/50" />
    );
  }

  // Logged-out: prompt to sign in (submitting requires an account).
  if (!session?.user) {
    return (
      <div className="rounded-xl border border-border bg-card p-6 text-center">
        <h2 className="text-lg font-semibold text-foreground">
          {m.contact_signin_heading()}
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          {m.contact_signin_body()}
        </p>
        <Link
          to="/login"
          search={{ redirectUrl: "/contact" }}
          className="mt-4 inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
        >
          {m.nav_sign_in()}
        </Link>
      </div>
    );
  }

  // Success confirmation.
  if (submitted) {
    return (
      <div className="rounded-xl border border-success/30 bg-success/5 p-6 text-center">
        <CheckCircle2
          className="mx-auto h-10 w-10 text-success"
          aria-hidden="true"
        />
        <h2 className="mt-3 text-lg font-semibold text-foreground">
          {m.contact_success_heading()}
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          {type === "proposal"
            ? m.contact_success_body_idea()
            : m.contact_success_body_bug()}
        </p>
        <Button
          type="button"
          variant="outline"
          className="mt-4"
          onClick={() => setSubmitted(false)}
        >
          {m.contact_send_another()}
        </Button>
      </div>
    );
  }

  const meta = TYPE_META[type];

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-xl border border-border bg-card p-6 space-y-5"
    >
      <fieldset>
        <legend className="mb-2 text-sm font-medium text-foreground">
          {m.contact_whats_this()}
        </legend>
        <div className="flex gap-3">
          <TypeButton
            type="proposal"
            active={type === "proposal"}
            onClick={() => setType("proposal")}
          />
          <TypeButton
            type="bug"
            active={type === "bug"}
            onClick={() => setType("bug")}
          />
        </div>
      </fieldset>

      <div className="space-y-1.5">
        <Label htmlFor="contact-title">{m.contact_label_title()}</Label>
        <Input
          id="contact-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={meta.titleHint()}
          required
          minLength={TITLE_MIN}
          maxLength={TITLE_MAX}
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="contact-message">{m.contact_label_message()}</Label>
        <Textarea
          id="contact-message"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={meta.bodyHint()}
          required
          minLength={MESSAGE_MIN}
          maxLength={MESSAGE_MAX}
          rows={6}
        />
        <p className="text-right text-xs text-muted-foreground">
          {message.length}/{MESSAGE_MAX}
        </p>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/20 bg-destructive/10 p-3">
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}

      <Button type="submit" className="w-full" disabled={loading}>
        {loading ? (
          <span className="flex items-center gap-2">
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-primary-foreground/30 border-t-primary-foreground" />
            {m.contact_sending()}
          </span>
        ) : (
          <span className="flex items-center gap-2">
            <Send className="h-4 w-4" aria-hidden="true" />
            {type === "proposal"
              ? m.contact_send_idea()
              : m.contact_send_report()}
          </span>
        )}
      </Button>
    </form>
  );
}
